import { Switch } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { XeroIcon } from "../../../assets/icons/xero";
import inventoryPlannerIcon from "../../../assets/img/inventoryPlanner.png";
import mintSoftIcon from "../../../assets/img/mintsoft.webp";
import shipheroIcon from "../../../assets/img/shiphero.webp";
import shopifyBag from "../../../assets/img/shopify-bag.svg";
import { ButtonT1 } from "../../../components/atoms/Button";
import { ConfirmationModal } from "../../../components/organisms/Modal/confirmationModal";
import { useNotification } from "../../../hooks/useNotification";
import {
  useGetIntegrationExternalSystemsQuery,
  useUpdateIntegrationStatusByCodeMutation,
} from "../../../store/apiSlice/integrationApiSlice";
import { useTriggerSyncMutation } from "../../../store/apiSlice/notificationApiSlice";
import {
  useDeleteTrackingAccountMutation,
  useGetTrackingAccountsQuery,
  useSaveTrackingAccountMutation,
} from "../../../store/apiSlice/oceanTrackingApiSlice";
import { apiError } from "../../oceanTracking/utils";
import {
  ConnectionStatusDiv,
  IntegrationCardDiv,
  IntegrationGridDiv,
  IntegrationToolbarActions,
  StyledButtonContainerDiv,
  StyledDisabledText,
  StyledRightActionsDiv,
  StyledSyncMenu,
  StyledSyncNowWrap,
} from "../index.styles";

import { isSystemConnected } from "../../../utils/xeroIntegration";
import { IntegrationOutlineButton } from "./IntegrationOutlineButton";

/** Integrations that support manual Sync Now from the integrations screen. */
const SYNCABLE_SYSTEM_CODES = new Set(["xero", "shopify", "inventory_planner"]);

const SYNCABLE_SYSTEM_LABELS = {
  xero: "Xero",
  shopify: "Shopify",
  inventory_planner: "Inventory Planner",
};

/**
 * Whether an external system has at least one active authenticated connection
 * and can be offered in the Sync Now menu.
 */
export const isConnectedSyncTarget = sys => {
  if (!sys?.code || !SYNCABLE_SYSTEM_CODES.has(sys.code)) return false;
  const configs = Array.isArray(sys.connect_config) ? sys.connect_config : [];
  if (sys.code === "shopify") {
    return configs.some(store => store?.is_authenticated && store?.is_active !== false);
  }
  return isSystemConnected(sys);
};

function InventoryPlannerCard({ data = {} }) {
  const navigate = useNavigate();
  const {
    name,
    description,
    code,
    connect_config: [firstConfig],
  } = data;

  const { is_authenticated, is_active } = firstConfig || {};
  const Icon = integrationConfig["inventory_planner"].icon;
  const classForConnection = firstConfig && is_authenticated ? "green" : "red";
  const [updateInventoryStatus] = useUpdateIntegrationStatusByCodeMutation();

  const handleActiveStatus = async is_active => {
    await updateInventoryStatus({ data: { is_active }, params: "inventory_planner" });
  };

  const handleConnect = () => {
    navigate(code);
  };

  const stopBubblingEvent = e => {
    e.stopPropagation();
  };

  return (
    <IntegrationCardDiv onClick={handleConnect}>
      <div className="cardHeader">
        <div className="headerWithIcon">
          {Icon}
          <div className="headerName">
            <div className="nameOfHeader">{name}</div>
            <div className="headerType">Accounting</div>
          </div>
        </div>

        {firstConfig && (
          <ConnectionStatusDiv className={classForConnection}>
            {firstConfig?.is_authenticated ? "Connected" : "Not Connected"}
          </ConnectionStatusDiv>
        )}
      </div>

      <div className="description">{description}</div>

      {firstConfig ? (
        <div className="switchStatus  bottomRight" onClick={stopBubblingEvent}>
          <Switch title="enabled" onChange={handleActiveStatus} checked={is_active} />
          {is_active ? "Enabled" : <StyledDisabledText>Disabled</StyledDisabledText>}
        </div>
      ) : (
        <IntegrationOutlineButton
          className="bottomRight"
          title="Connect"
          onClick={event => {
            event.stopPropagation();
            handleConnect();
          }}
        />
      )}
    </IntegrationCardDiv>
  );
}

function MintSoftCard({ data = {} }) {
  const {
    name,
    description,
    code,
    connect_config: [firstConfig],
  } = data;

  const { is_authenticated, is_active } = firstConfig || {};
  const Icon = integrationConfig[code].icon;
  const classForConnection = firstConfig && is_authenticated ? "green" : "red";

  return (
    <IntegrationCardDiv>
      <div className="cardHeader">
        <div className="headerWithIcon">
          {Icon}
          <div className="headerName">
            <div className="nameOfHeader">{name}</div>
            <div className="headerType">Accounting</div>
          </div>
        </div>

        {firstConfig && (
          <ConnectionStatusDiv className={classForConnection}>
            {firstConfig?.is_authenticated ? "Connected" : "Not Connected"}
          </ConnectionStatusDiv>
        )}
      </div>

      <div className="description">{description}</div>

      {firstConfig ? (
        <div className="switchStatus  bottomRight">
          <Switch title="enabled" checked={is_active} />
          Enabled
        </div>
      ) : (
        <IntegrationOutlineButton className="bottomRight" title="Connect" disabled />
      )}
    </IntegrationCardDiv>
  );
}

function ShipheroCard({ data = {} }) {
  const {
    name,
    description,
    code,
    connect_config: [firstConfig],
  } = data;

  const { is_authenticated, is_active } = firstConfig || {};
  const Icon = integrationConfig[code].icon;
  const classForConnection = firstConfig && is_authenticated ? "green" : "red";

  return (
    <IntegrationCardDiv>
      <div className="cardHeader">
        <div className="headerWithIcon">
          {Icon}
          <div className="headerName">
            <div className="nameOfHeader">{name}</div>
            <div className="headerType">Accounting</div>
          </div>
        </div>

        {firstConfig && (
          <ConnectionStatusDiv className={classForConnection}>
            {firstConfig?.is_authenticated ? "Connected" : "Not Connected"}
          </ConnectionStatusDiv>
        )}
      </div>

      <div className="description">{description}</div>

      {firstConfig ? (
        <div className="switchStatus  bottomRight">
          <Switch title="enabled" checked={is_active} />
          Enabled
        </div>
      ) : (
        <IntegrationOutlineButton className="bottomRight" title="Connect" disabled />
      )}
    </IntegrationCardDiv>
  );
}

function ShopifyCard({ data = {} }) {
  const navigate = useNavigate();
  const { name, description, code, connect_config: stores = [] } = data;

  const Icon = integrationConfig[code].icon;
  // Multi-store integration: the card reflects the overall connection state,
  // per-store enable/disable lives on the stores list (no global switch here).
  const hasConnectedStore = stores.some(store => store.is_authenticated);
  const classForConnection = hasConnectedStore ? "green" : "red";

  const handleConnect = () => {
    navigate(code);
  };

  return (
    <IntegrationCardDiv onClick={handleConnect}>
      <div className="cardHeader">
        <div className="headerWithIcon">
          {Icon}
          <div className="headerName">
            <div className="nameOfHeader">{name}</div>
            <div className="headerType">Ecommerce</div>
          </div>
        </div>

        {stores.length > 0 && (
          <ConnectionStatusDiv className={classForConnection}>
            {hasConnectedStore ? "Connected" : "Not Connected"}
          </ConnectionStatusDiv>
        )}
      </div>

      <div className="description">{description}</div>

      {stores.length > 0 ? (
        <div className="switchStatus bottomRight">
          {stores.length} {stores.length === 1 ? "Store" : "Stores"}
        </div>
      ) : (
        <IntegrationOutlineButton
          className="bottomRight"
          title="Connect"
          onClick={event => {
            event.stopPropagation();
            handleConnect();
          }}
        />
      )}
    </IntegrationCardDiv>
  );
}

function XeroCard({ data = {} }) {
  const navigate = useNavigate();
  const {
    name,
    description,
    code,
    connect_config: [firstConfig],
  } = data;

  const { is_authenticated, is_active } = firstConfig || {};
  const Icon = integrationConfig[code].icon;
  const classForConnection = firstConfig && is_authenticated ? "green" : "red";

  const [updateXeroStatus] = useUpdateIntegrationStatusByCodeMutation();

  const handleActiveStatus = async is_active => {
    await updateXeroStatus({ data: { is_active }, params: "xero" });
  };

  const handleConnect = e => {
    navigate(code);
  };

  const stopBubblingEvent = e => {
    e.stopPropagation();
  };

  return (
    <IntegrationCardDiv onClick={handleConnect}>
      <div className="cardHeader">
        <div className="headerWithIcon">
          {Icon}
          <div className="headerName">
            <div className="nameOfHeader">{name}</div>
            <div className="headerType">Accounting</div>
          </div>
        </div>

        {firstConfig && (
          <ConnectionStatusDiv className={classForConnection}>
            {firstConfig?.is_authenticated ? "Connected" : "Not Connected"}
          </ConnectionStatusDiv>
        )}
      </div>

      <div className="description">{description}</div>

      {firstConfig ? (
        <div className="switchStatus  bottomRight" onClick={stopBubblingEvent}>
          <Switch title="enabled" onChange={handleActiveStatus} checked={is_active} />
          {is_active ? "Enabled" : <StyledDisabledText>Disabled</StyledDisabledText>}
        </div>
      ) : (
        <IntegrationOutlineButton
          className="bottomRight"
          title="Connect"
          onClick={event => {
            event.stopPropagation();
            handleConnect(event);
          }}
        />
      )}
    </IntegrationCardDiv>
  );
}

function TrackingProviderCard({ account, canChange }) {
  const navigate = useNavigate();
  const notify = useNotification();
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [saveTrackingAccount] = useSaveTrackingAccountMutation();
  const [deleteTrackingAccount, { isLoading: isRemoving }] = useDeleteTrackingAccountMutation();
  const isConnected = account?.connection_status === "connected";
  const manageUrl = `/integration/findteu?account=${account.id}`;

  const handleActiveStatus = async is_active => {
    await saveTrackingAccount({ id: account.id, is_active }).unwrap();
  };

  const handleRemove = async () => {
    if (isRemoving) return;
    try {
      await deleteTrackingAccount(account.id).unwrap();
      setShowRemoveModal(false);
      notify({
        type: "success",
        message: `${account.provider_name} integration removed.`,
      });
    } catch (error) {
      notify({ type: "error", message: apiError(error) });
    }
  };

  return (
    <>
      <IntegrationCardDiv onClick={() => navigate(manageUrl)}>
        <div className="cardHeader">
          <div className="headerName">
            <div className="nameOfHeader" style={{ fontWeight: "bold" }}>
              {account.provider_name}
            </div>
            <div className="headerType">
              {account.capabilities?.length
                ? `${account.capabilities.length} tracking ${account.capabilities.length === 1 ? "type" : "types"}`
                : "Shipment tracking"}
            </div>
          </div>
          <ConnectionStatusDiv className={isConnected ? "green" : "red"}>
            {isConnected ? "Connected" : "Not Connected"}
          </ConnectionStatusDiv>
        </div>
        <div className="description">Track shipments through {account.provider_name}.</div>
        {canChange && !account.is_active && (
          <div className="bottomLeft" onClick={event => event.stopPropagation()}>
            <ButtonT1
              title="Remove"
              $variant="danger"
              $size="small"
              loading={isRemoving}
              onClickCallback={() => setShowRemoveModal(true)}
            />
          </div>
        )}
        <div className="switchStatus bottomRight" onClick={event => event.stopPropagation()}>
          <Switch
            title="enabled"
            checked={account.is_active}
            disabled={!canChange}
            onChange={handleActiveStatus}
          />
          {account.is_active ? "Enabled" : <StyledDisabledText>Disabled</StyledDisabledText>}
        </div>
      </IntegrationCardDiv>
      {showRemoveModal && (
        <ConfirmationModal
          closeModal={() => setShowRemoveModal(false)}
          handleModalWork={handleRemove}
          titleOfModal={`Remove ${account.provider_name} Integration`}
          description={`Removing ${account.provider_name} will stop automated tracking through this provider. Existing shipment tracking history will be retained.`}
          labelForConfirmCheck={`I understand tracking through ${account.provider_name} will stop.`}
          btnName="Remove"
          btnVariant="danger"
          isLoading={isRemoving}
        />
      )}
    </>
  );
}

export const integrationConfig = {
  xero: {
    icon: <XeroIcon />,
    CardComponent: XeroCard,
  },
  shopify: {
    icon: <img src={shopifyBag} alt="shopify" width={32} height={32} />,
    CardComponent: ShopifyCard,
  },
  inventory_planner: {
    icon: <img src={inventoryPlannerIcon} alt="inventory_planner" />,
    CardComponent: InventoryPlannerCard,
  },
  shiphero: {
    icon: <img src={shipheroIcon} alt="ShipHero" width={32} height={32} />,
    CardComponent: ShipheroCard,
  },
  mint_soft: {
    icon: <img src={mintSoftIcon} alt="Mint Soft" width={32} height={32} />,
    CardComponent: MintSoftCard,
  },
};

export function IntegrationGrid() {
  const { externalSystems, user } = useSelector(
    ({ integration: { externalSystems }, auth: { user } }) => ({
      externalSystems,
      user,
    }),
  );
  const navigate = useNavigate();
  const notify = useNotification();
  const { data: trackingAccounts = [] } = useGetTrackingAccountsQuery();
  const canChange = user?.is_superuser || user?.groups?.some(group => Number(group.id) === 1);
  const [triggerSync, { isLoading: isSyncing }] = useTriggerSyncMutation();
  const [isSyncMenuOpen, setIsSyncMenuOpen] = useState(false);
  const syncMenuRef = useRef(null);

  useGetIntegrationExternalSystemsQuery();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const systems = Array.isArray(externalSystems) ? externalSystems : externalSystems?.results || [];

  const connectedSyncTargets = useMemo(
    () =>
      systems
        .filter(isConnectedSyncTarget)
        .map(sys => ({
          code: sys.code,
          name: sys.name || SYNCABLE_SYSTEM_LABELS[sys.code] || sys.code,
        })),
    [systems],
  );

  const closeSyncMenu = () => setIsSyncMenuOpen(false);

  // Same click-outside pattern as TopBar PopupModal, scoped to button + menu wrap.
  useEffect(() => {
    if (!isSyncMenuOpen) {
      return undefined;
    }
    const handleClickOutside = event => {
      if (syncMenuRef.current && !syncMenuRef.current.contains(event.target)) {
        closeSyncMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSyncMenuOpen]);

  const handleTriggerSync = async ({ type, code, label }) => {
    closeSyncMenu();
    try {
      const body = type === "all" ? { type: "all" } : { type: "single", code };
      const response = await triggerSync(body).unwrap();
      notify({
        type: "success",
        message:
          response.detail ||
          (type === "all"
            ? "Triggered sync for all connected apps. Watch Background tasks for progress."
            : `Triggered ${label} sync. Watch Background tasks for progress.`),
      });
    } catch (error) {
      notify({
        type: "error",
        message:
          error?.data?.detail ||
          (type === "all" ? "Failed to trigger sync for all apps" : `Failed to trigger ${label} sync`),
      });
    }
  };

  return (
    <div>
      <StyledButtonContainerDiv>
        <IntegrationToolbarActions>
          <ButtonT1
            $variant="primaryLight"
            title="Add Tracking Provider"
            onClickCallback={() => navigate("/integration/tracking-provider/new")}
          />
        </IntegrationToolbarActions>
        <IntegrationToolbarActions>
          <StyledRightActionsDiv>
            {connectedSyncTargets.length > 0 && (
              <StyledSyncNowWrap ref={syncMenuRef}>
                <ButtonT1
                  $variant="primaryLight"
                  title={isSyncing ? "Syncing…" : "Sync Now"}
                  disabled={isSyncing}
                  onClickCallback={() => setIsSyncMenuOpen(open => !open)}
                />
                {isSyncMenuOpen && (
                  <StyledSyncMenu>
                    <button
                      type="button"
                      disabled={isSyncing}
                      onClick={() => handleTriggerSync({ type: "all", label: "all apps" })}
                    >
                      Sync all
                    </button>
                    {connectedSyncTargets.map(target => (
                      <button
                        key={target.code}
                        type="button"
                        disabled={isSyncing}
                        onClick={() =>
                          handleTriggerSync({
                            type: "single",
                            code: target.code,
                            label: target.name,
                          })
                        }
                      >
                        Sync {target.name}
                      </button>
                    ))}
                  </StyledSyncMenu>
                )}
              </StyledSyncNowWrap>
            )}
            <ButtonT1
              $variant="primaryLight"
              title="View Sync History"
              onClickCallback={() => navigate("/integration/sync_history")}
            />
          </StyledRightActionsDiv>
        </IntegrationToolbarActions>
      </StyledButtonContainerDiv>

      <IntegrationGridDiv>
        {trackingAccounts.map(account => (
          <TrackingProviderCard key={account.id} account={account} canChange={canChange} />
        ))}
        {systems.map(sys => {
          const { code } = sys;
          const config = integrationConfig[code];
          if (!config) return null;
          const { CardComponent } = config;
          return <CardComponent key={code} data={sys} />;
        })}
      </IntegrationGridDiv>
    </div>
  );
}

export { InventoryPlannerCard, MintSoftCard, ShipheroCard, ShopifyCard, XeroCard };
